<script setup lang="ts">
import { SaleSteppedPrice } from '@lib/shared';

import type { AuctionItemEntity } from '~/apiClient';

// PROPS
interface Props {
    auction: AuctionItemEntity;
}
const { auction } = defineProps<Props>();

// COMPOSABLES
const { tn, tt } = useTypedI18n();
const { companyScopeIds } = useAuthUtils();

// DATA
const isSeller = computed(() => isSellerOfSale(auction, companyScopeIds.value));
const steppedPrice = computed(() => SaleSteppedPrice.createDefault());
// Null once the vehicle is sold off platform: the range derives from the reserve price.
const range = computed(() => {
    const { computedMinMarketPrice, computedMaxMarketPrice } = auction;

    if (computedMinMarketPrice === null || computedMaxMarketPrice === null) return null;

    return {
        min: steppedPrice.value.getStepRoundedReservePrice(computedMinMarketPrice),
        max: steppedPrice.value.getStepRoundedReservePrice(computedMaxMarketPrice),
    };
});
const shouldShow = computed(() => !isSeller.value);
</script>

<template>
    <div v-if="shouldShow && range" class="text-muted text-sm">
        <span class="inline-flex items-center gap-1">
            {{ tt('auction.estimatedMarketValue.label') }}
            <BaseTooltip :text="tt('auction.estimatedMarketValue.tooltip')">
                <UIcon name="lucide:info" class="size-4 shrink-0" />
            </BaseTooltip>
        </span>
        <p class="text-base font-semibold">{{ tn(range.min, 'currency') }} – {{ tn(range.max, 'currency') }}</p>
    </div>
</template>
